/**
 * SectionCard Component - V4 Design System
 * Collapsible card component for settings sections
 * Uses design tokens for consistent styling
 */

import React, { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { cn } from "../../../utils/tw";

interface SectionCardProps {
  title?: string;
  subtitle?: string;
  actions?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
  noPadding?: boolean;
}

const SectionCard: React.FC<SectionCardProps> = ({ 
  title, 
  subtitle,
  actions, 
  className, 
  children,
  defaultOpen = true,
  noPadding = false
}) => {
  const [open, setOpen] = useState(defaultOpen);
  
  return (
    <div className={cn(
      'mb-6 bg-ds-bg-card rounded-lg border border-ds-bg-border shadow-elev-1 transition-shadow hover:shadow-elev-2',
      className
    )}>
      {title && (
        <div 
          className="flex items-center justify-between px-6 py-4 cursor-pointer hover:bg-ds-bg-hover transition-colors rounded-t-lg" 
          onClick={() => setOpen(!open)}
        >
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold select-none text-text-primary text-lg">
              {title}
            </h3>
            {subtitle && (
              <p className="text-sm text-text-secondary mt-0.5">
                {subtitle}
              </p>
            )}
          </div>
          <div className="flex items-center gap-3 ml-4">
            {actions}
            <button
              type="button"
              className="p-1.5 rounded-md hover:bg-ds-bg-hover transition-colors text-text-secondary"
              aria-label={open ? "Collapse section" : "Expand section"}
            >
              {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </button>
          </div>
        </div>
      )}
      <div className={cn(
        "overflow-hidden transition-all duration-300 ease-in-out",
        open ? "max-h-[5000px] opacity-100" : "max-h-0 opacity-0"
      )}>
        <div className={cn(
          noPadding ? "" : "px-6 pb-6",
          title && !noPadding && "pt-2"
        )}>
          {children}
        </div>
      </div>
    </div>
  );
};

export default SectionCard;
